import { Lexer } from 'marked';
import { guideCardToPlainText } from './guide_card_text';

const esc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;')
  .replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const decode = value => String(value || '').replace(/&(#x[\da-f]+|#\d+|amp|lt|gt|quot|apos|nbsp);/gi, (match, entity) => {
  const name = entity.toLowerCase();
  if (name[0] !== '#') return { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' }[name];
  const point = name[1] === 'x' ? parseInt(name.slice(2), 16) : parseInt(name.slice(1), 10);
  return point > 0 && point <= 0x10ffff && !(point >= 0xd800 && point <= 0xdfff) ? String.fromCodePoint(point) : match;
});
const SAFE_HREF = /^(https?:|mailto:)/i;

const inline = (tokens) => (tokens || []).map((t) => {
  switch (t.type) {
    case 'text': return t.tokens ? inline(t.tokens) : esc(decode(t.text));
    case 'escape': return esc(t.text);
    case 'strong': return `<strong>${inline(t.tokens)}</strong>`;
    case 'em': return `<em>${inline(t.tokens)}</em>`;
    case 'del': return `<del>${inline(t.tokens)}</del>`;
    case 'codespan': return `<code>${esc(t.text)}</code>`;
    case 'br': return '<br>';
    case 'link': {
      const href = SAFE_HREF.test(t.href || '') ? esc(t.href) : '';
      const body = inline(t.tokens);
      return href ? `<a href="${href}">${body}</a>` : body;
    }
    case 'image': return esc(t.text || '');
    case 'html': return esc(t.raw ?? t.text ?? '');
    default: return esc(t.raw ?? t.text ?? '');
  }
}).join('');

const block = (tokens, renderCards) => (tokens || []).map((t) => {
  switch (t.type) {
    case 'heading': return `<h${t.depth}>${inline(t.tokens)}</h${t.depth}>`;
    case 'paragraph': return `<p>${inline(t.tokens)}</p>`;
    case 'blockquote': return `<blockquote>${block(t.tokens, renderCards)}</blockquote>`;
    case 'list': {
      const tag = t.ordered ? 'ol' : 'ul';
      const start = t.ordered && t.start !== 1 ? ` start="${t.start}"` : '';
      const items = t.items.map((it) => `<li>${it.task ? (it.checked ? '☑ ' : '☐ ') : ''}${block(it.tokens, renderCards)}</li>`).join('');
      return `<${tag}${start}>${items}</${tag}>`;
    }
    case 'code': {
      const lang = (t.lang || '').trim();
      if (renderCards) {
        const card = guideCardToPlainText(lang, t.text);
        if (card !== null) {
          const isAnswer = lang.toLowerCase() === 'gab-ui';
          return isAnswer ? block(Lexer.lex(card), false) : `<p>${esc(card).replace(/\n/g, '<br>')}</p>`;
        }
      }
      const cls = /^[A-Za-z0-9_+\-.#]+$/.test(lang) ? ` class="language-${esc(lang)}"` : '';
      return `<pre><code${cls}>${esc(t.text)}</code></pre>`;
    }
    case 'table': {
      const head = `<thead><tr>${t.header.map((c) => `<th>${inline(c.tokens)}</th>`).join('')}</tr></thead>`;
      const body = `<tbody>${t.rows.map((r) => `<tr>${r.map((c) => `<td>${inline(c.tokens)}</td>`).join('')}</tr>`).join('')}</tbody>`;
      return `<table>${head}${body}</table>`;
    }
    case 'text': return inline(t.tokens || [{ type: 'text', text: t.text }]);
    case 'hr': return '<hr>';
    case 'html': return `<p>${esc(t.raw ?? t.text ?? '')}</p>`;
    case 'space': return '';
    default: return esc(t.raw ?? t.text ?? '');
  }
}).join('');

export default function markdownToSemanticHtml(markdown, { renderCards = true } = {}) {
  const raw = typeof markdown === 'string' ? markdown : String(markdown || '');
  if (!raw.trim()) return '';
  try {
    return `<html><head><meta charset="utf-8"></head><body>${block(Lexer.lex(raw, { gfm: true }), renderCards)}</body></html>`;
  } catch (err) {
    console.warn('Failed to serialize markdown for copy:', err);
    return '';
  }
}
